import { ArrowLeft, X } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "../../components/button"
import type { AgentChatSlots, SidePanelKey } from "./types"

interface SidePanelSlotProps {
  slots?: AgentChatSlots
  activePanel?: SidePanelKey | null
  onClose?: () => void
  isMobile?: boolean
  className?: string
}

const PANEL_TITLES: Record<SidePanelKey, string> = {
  browser: "Browser",
  artifact: "Artifact",
  workspace: "Workspace",
}

/**
 * Renders the host-provided side-panel content keyed by `activePanel`.
 * On desktop, intended to be placed alongside the chat column. On
 * mobile, it stacks above the chat as a full-screen overlay with a
 * "back to chat" affordance.
 *
 * The block only owns the slot frame (header + close); the actual
 * panel content (webcontainer iframe, artifact preview, etc.) is the
 * host's responsibility — keeping the package free of webcontainer
 * dependencies.
 */
export function SidePanelSlot({
  slots,
  activePanel,
  onClose,
  isMobile = false,
  className,
}: SidePanelSlotProps) {
  if (!activePanel || !slots) return null
  const node =
    activePanel === "browser"
      ? slots.browserPanel
      : activePanel === "artifact"
        ? slots.artifactPanel
        : slots.workspacePanel

  if (!node) return null

  return (
    <section
      data-slot="agent-chat-side-panel"
      data-panel={activePanel}
      className={cn(
        "flex min-h-0 min-w-0 flex-col bg-background",
        isMobile && "absolute inset-0 z-10",
        className,
      )}
      aria-label={`${PANEL_TITLES[activePanel]} panel`}
    >
      <header className="flex h-10 shrink-0 items-center gap-1.5 border-b border-black/10 px-2 dark:border-white/10">
        {isMobile && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Back to chat"
          >
            <ArrowLeft />
          </Button>
        )}
        <span className="font-heading text-sm">
          {PANEL_TITLES[activePanel]}
        </span>
        <div className="flex-1" />
        {!isMobile && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X />
          </Button>
        )}
      </header>
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{node}</div>
    </section>
  )
}
